"use client";

import { useState } from "react";
import {
  BookOpen,
  ChevronRight,
  FileText,
  Folder,
  FolderOpen,
} from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shadcn-components/ui/collapsible";
import { cn } from "@/lib/utils";
import {
  getSdkDocSummary,
  getSdkDocTree,
  type SdkDocNode,
} from "@/lib/custom-indicators/sdk-docs";
import { useCustomIndicatorProjectStore } from "@/lib/custom-indicators/store";

function DocsTreeItem({
  node,
  depth = 0,
  selectedId,
  onSelect,
}: {
  node: SdkDocNode;
  depth?: number;
  selectedId: string | null;
  onSelect: (node: SdkDocNode) => void;
}) {
  const isFolder = (node.children?.length ?? 0) > 0;
  const [open, setOpen] = useState(node.defaultOpen ?? false);
  const isSelected = selectedId === node.id && !isFolder;

  if (!isFolder) {
    return (
      <button
        type="button"
        className={cn(
          "flex w-full items-center gap-1.5 rounded-sm py-0.5 pr-2 text-left text-[11px]",
          isSelected
            ? "bg-primary/10 text-primary"
            : "text-foreground/90 hover:bg-muted/60",
        )}
        style={{ paddingLeft: `${depth * 12 + 8}px` }}
        onClick={() => onSelect(node)}
      >
        <BookOpen className="h-3.5 w-3.5 shrink-0 text-violet-500/80" />
        <span className="truncate">{node.label}</span>
      </button>
    );
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger
        type="button"
        className={cn(
          "flex w-full items-center gap-1 rounded-sm py-0.5 pr-2 text-left text-[11px] font-medium text-foreground/90",
          "hover:bg-muted/60",
        )}
        style={{ paddingLeft: `${depth * 12 + 4}px` }}
      >
        <ChevronRight
          className={cn(
            "h-3 w-3 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-90",
          )}
        />
        {open ? (
          <FolderOpen className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        ) : (
          <Folder className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        )}
        <span className="truncate">{node.label}</span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        {node.children?.map((child) => (
          <DocsTreeItem
            key={child.id}
            node={child}
            depth={depth + 1}
            selectedId={selectedId}
            onSelect={onSelect}
          />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}

export default function NavigatorDocsSection() {
  const tree = getSdkDocTree();
  const activeEditorTarget = useCustomIndicatorProjectStore(
    (s) => s.activeEditorTarget
  );
  const openSdkDoc = useCustomIndicatorProjectStore((s) => s.openSdkDoc);

  const selectedDocId =
    activeEditorTarget.kind === "sdk-doc"
      ? activeEditorTarget.docId
      : null;

  const handleSelect = (node: SdkDocNode) => {
    const isFolder = (node.children?.length ?? 0) > 0;
    if (isFolder) return;
    openSdkDoc(node.id);
  };

  const summary = selectedDocId
    ? getSdkDocSummary(selectedDocId)
    : undefined;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain py-1">
        {tree.children?.map((child) => (
          <DocsTreeItem
            key={child.id}
            node={child}
            selectedId={selectedDocId}
            onSelect={handleSelect}
          />
        ))}
      </div>
      {summary ? (
        <div className="shrink-0 border-t border-border bg-muted/20 px-3 py-2">
          <div className="mb-1 flex items-center gap-1.5">
            <FileText className="h-3 w-3 text-muted-foreground" />
            <p className="truncate text-[11px] font-medium">
              Open in editor →
            </p>
          </div>
          <p className="text-[10px] leading-relaxed text-muted-foreground">
            {summary}
          </p>
        </div>
      ) : (
        <div className="shrink-0 border-t border-border px-3 py-2">
          <p className="text-[10px] text-muted-foreground">
            Select a topic to open the SDK reference in the editor.
          </p>
        </div>
      )}
    </div>
  );
}
